<template>
  <div>
    <div>
      <aha-modal
        @hide="hide()"
        :title="''"
        action-position="justify-end"
      >
        <div class="custom-title">
          {{ title_CreateNewFolder }} <IconNeedUpgradePlan
            v-if="planBenefit.teamWorkCollaboration"
            ref="iconNeedUpgradePlanComponent"
            type="'folderManagement'"
            :noPermission="planBenefit.teamWorkCollaboration && !planBenefit.teamWorkCollaboration.folderManagement"
            :upgradePlan="planBenefit.teamWorkCollaboration.folderManagement_planName"
            featureName="Create Folder"
            souce="Presentation List"
            left="-80px"
          />
        </div>
        <form @submit.prevent="handleCreateNewFolder()">
          <aha-input
            focus
            v-model="folderName"
            id="input_folder-name"
            :maxLength="limitFolderName"
          />
        </form>
        <template #action>
          <aha-antd-button
            v-aha-emit-action
            variant="secondary"
            :disabled="isCreatingFolder"
            @click="hide()"
          >
            <span class="font-bold">{{ $i18nPresenter.t("Cancel") }}</span>
          </aha-antd-button>
          <aha-antd-button
            v-aha-emit-action
            name="create-new-folder"
            :disabled="isCreatingFolder || folderName.length === 0"
            @click="handleCreateNewFolder()"
            :submitting="isCreatingFolder"
            is-loading-inline
            class="ml-4"
          >
            {{ $i18nPresenter.t("Create Folder") }}
          </aha-antd-button>
        </template>
        <p class="pt-2 text-red-50" v-if="textError">
          {{ textError }}
        </p>
      </aha-modal>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'

// api
import FolderApi from '@/api/folder'
import PresentationApi from '@/api/presentation'

// utils
import utils from '@/util'
import EventBus from '@/eventBus'

// constants
import constants from '@/constant'
import configs from '@/config'

import IconNeedUpgradePlan from '@/components-v2/my-plan/IconNeedUpgradePlan.vue'

const { MAX_FOLDER_DEPTH } = configs

export default {
  components: {
    IconNeedUpgradePlan,
  },
  props: {
    presentationIds: {
      type: Array,
      default: () => [],
    },
    visible: {
      type: Boolean,
      default: false,
    },
    funcMovePresn: {
      type: Function,
      default: null,
    },
    folders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      folderName: '',
      isCreatingFolder: false,
      limitFolderName: 100,
      textError: '',
      title_CreateNewFolder: this.$i18nPresenter.t('Create New Folder'),
    }
  },
  computed: {
    ...mapState({
      selectedFolderId: (state) => state.selections.selectedFolderId,
      workingMode: (state) => state.workingMode,
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
      currentFolderDepth: (state) => state.currentFolderDepth,
      planBenefit: (state) => state.planBenefit,
      listFolders: (state) => state.folders,
    }),
    ...mapGetters('featureToggles', ['canUseMovePresentations']),
    isRouteFolderDetails() {
      return this.$route.name === 'FolderDetail'
    },
    isRouteShareFolderDetails() {
      return this.$route.name === 'ShareFolderDetail'
    },
    isMaxFolderDepth() {
      return this.currentFolderDepth >= parseInt(MAX_FOLDER_DEPTH, 10)
    },
  },
  methods: {
    ...mapActions(['setErrorOnInternalServerAction', 'setFoldersAction']),
    ...mapActions('movePresentation', [
      'setMyPresentationFoldersAction',
    ]),
    trackEvent(label, value) {
      utils.mixpanelTracking(
        constants.eventCategories.PRESENTATION_LIST,
        label,
        value,
      )
    },
    handleCreateNewFolder() {
      if (this.planBenefit.teamWorkCollaboration && !this.planBenefit.teamWorkCollaboration.folderManagement) {
        this.$refs.iconNeedUpgradePlanComponent.openTooltip()
        return
      }
      utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'New > Create New Folder > Submit', `Folder name: ${this.folderName}`, null, {
        isTeamFolder: this.workingMode === constants.workspace.TEAM,
        role: this.currentUser.accountRole || null,
        teamId: this.currentAccount.id || null,
        parentFolderId: this.selectedFolderId,
        folderDepth: this.currentFolderDepth || 0,
      })

      if (this.folderName.trim() === '') {
        this.textError = this.$i18nPresenter.t('Please enter a name for folder.')
        return
      }

      this.isCreatingFolder = true
      if (this.isRouteFolderDetails || this.isRouteShareFolderDetails) {
        this._handleCreateSubFolder()
      } else {
        this._handleCreateFolder()
      }
    },

    async _handleCreateSubFolder() {
      try {
        if (this.isMaxFolderDepth) return
        const result = await FolderApi.createSubFolder({
          parentFolderId: this.selectedFolderId,
          folderName: this.folderName.trim(),
        })
        this._processCreateFolderData(result)
        this.hide()
        EventBus.$emit('reload-presentations-and-folders')
      } catch (error) {
        Sentry.captureException(error)
        if (error.body.message === 'folderName must be unique') {
          this.textError = this.$i18nPresenter.t('This folder name is already in use, please enter something else.')
        } else {
          utils.showToastError('Cannot create folder. Please check your connection and try again.')
        }
      }
      this.isCreatingFolder = false
    },

    async _handleCreateFolder() {
      try {
        const result = await FolderApi.createFolder({
          folderName: this.folderName.trim(),
        })
        this._processCreateFolderData(result)
      } catch (error) {
        Sentry.captureException(error)
        if (error.body.message === 'folderName must be unique') {
          this.textError = this.$i18nPresenter.t('This folder name is already in use, please enter something else.')
        } else {
          utils.showToastError('Cannot create folder. Please check your connection and try again.')
        }

        this.trackEvent('New > Create New Folder > Failed', `Error: ${error.body.message}`)
      }

      this.isCreatingFolder = false
    },

    async _processCreateFolderData(result) {
      if (this.presentationIds.length) { // create new folder when moving presentation(s)
        await this.movePresentationToNewFolder(result)
      } else {
        utils.showToastSuccess(`${this.$i18nPresenter.t('Created folder')} "${utils.sanitize(this.folderName.trim())}"`)
      }

      this.hide()
      EventBus.$emit('reload-presentations-and-folders')
      utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'New > Create New Folder > Succeeded', `Folder name: ${this.folderName}`, null, {
        isTeamFolder: this.workingMode === constants.workspace.TEAM,
        role: this.currentUser.accountRole || null,
        teamId: this.currentAccount.id || null,
        folderId: (result && result.folder && result.folder.folderId) || null,
        parentFolderId: this.selectedFolderId,
        folderDepth: this.currentFolderDepth || 0,
      })
    },

    async movePresentationToNewFolder(result) {
      const payload = {
        presentationIds: this.presentationIds,
      }
      try {
        if (this.canUseMovePresentations && this.funcMovePresn) {
          const newFolders = [result.folder, ...this.folders]
          const newListFolders = [result.folder, ...this.listFolders]
          this.setFoldersAction(newListFolders)
          this.setMyPresentationFoldersAction(newFolders)
          this.$emit('updateDestinatedFolderId', result.folder.folderId)
          payload.folderId = result.folder.folderId
          this.funcMovePresn(payload)
          return
        }
        payload.presentation = { folderId: result.folder.folderId }
        await PresentationApi.patchPresentations(payload)
      } catch (error) {
        Sentry.captureException(error)
        utils.showToastError(this.$i18nPresenter.t('Move presentations failed'))
        this.trackEvent(
          'Presentation List > Move Presentation to new folder > Failed',
          error.body ? error.body.message : '',
        )
      }
    },
    hide() {
      this.$emit('hide')
    },
  },
}
</script>

<style scoped lang="scss">
.custom-title {
  margin-top: 0.5rem;
  margin-bottom: 0px;
  display: flex;
  overflow-wrap: break-word;
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 700;
  --tw-text-opacity: 1;
  color: rgba(10, 10, 10, var(--tw-text-opacity));
  position: absolute;
  top: 12px;
  left: 0;
  padding: 0 24px;
}
</style>
